import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { AssignEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/assignEmailCycleRPC";
import { getAPIClient } from "../../networking/APIClient";
import { RootStore } from "../../root/store/RootStore";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { useAssignEmailCycleRPCClientImpl } from "../rpcs/RPC";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";

type AssignEmailCycleRPCError = Exclude<
  AssignEmailCycleRPC.RPCError,
  | AssignEmailCycleRPC.RPCError.PendingSurveyAssignment
  | AssignEmailCycleRPC.RPCError.MissingEmailId
>;

type AssignEmailCycleRPCResult = LeoRPCResult<
  AssignEmailCycleRPC.Response,
  AssignEmailCycleRPC.Errors.Errors
>;

type AssignEmailCycleActionReturnType = Generator<
  Promise<AssignEmailCycleRPCResult>,
  void,
  AssignEmailCycleRPCResult
>;

const getInitialState = (): SnapshotIn<typeof AssignEmailCycleStore> => ({
  scheduleId: null,
  loadingAssignEmailCycle: false,
  assignEmailCycleRPCError: null,
});

export const AssignEmailCycleStore = types
  .model("AssignEmailCycleStore", {
    scheduleId: types.maybeNull(types.string),
    loadingAssignEmailCycle: types.boolean,
    assignEmailCycleRPCError: types.maybeNull(
      types.enumeration(
        "AssignEmailCycleRPCError",
        Object.values(AssignEmailCycleRPC.RPCError).filter(
          (rpcError): rpcError is AssignEmailCycleRPCError =>
            AssignEmailCycleRPC.RPCError.PendingSurveyAssignment !== rpcError,
        ),
      ),
    ),
  })
  .actions((store) => ({
    setCycleId: (scheduleId: string): void => {
      store.scheduleId = scheduleId;
    },
    assignEmailCycle: flow(function* (): AssignEmailCycleActionReturnType {
      store.loadingAssignEmailCycle = true;
      store.assignEmailCycleRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const respondentStore =
          getRoot<typeof RootStore>(store).projectsRootStore.projectDetailsStore
            .respondentStore;
        const request = new AssignEmailCycleRPC.Request(
          respondentStore.respondentSelection,
          new LeoUUID(store.scheduleId),
        );

        const result =
          yield useAssignEmailCycleRPCClientImpl(apiClient).execute(request);

        if ("response" in result) return;

        if (
          result.error instanceof
          AssignEmailCycleRPC.Errors.PendingSurveyAssignment
        ) {
          respondentStore.createOrAssignEmailCycleStore.setPendingSurveyAssignmentError(
            {
              respondentsAssigned: result.error.respondentsAssigned,
              respondentsSelected: result.error.respondentsSelected,
              respondentsUnassigned: result.error.respondentsUnassigned,
            },
          );
          return;
        }

        if (result.error instanceof AssignEmailCycleRPC.Errors.MissingEmailId) {
          respondentStore.createOrAssignEmailCycleStore.setMissingEmailIdError(
            result.error.count,
          );
          return;
        }

        switch (result.error.code) {
          case AssignEmailCycleRPC.RPCError.InvalidProjectId:
          case AssignEmailCycleRPC.RPCError.InvalidScheduleId:
          case AssignEmailCycleRPC.RPCError.EmailCycleInactive:
          case AssignEmailCycleRPC.RPCError.EmailCycleInProgress:
            store.assignEmailCycleRPCError = result.error.code;
            break;
          case AssignEmailCycleRPC.RPCError.PendingSurveyAssignment:
          case AssignEmailCycleRPC.RPCError.MissingEmailId:
            // Already handled above
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingAssignEmailCycle = false;
      }
    }),
    resetRpcError: (): void => {
      store.assignEmailCycleRPCError = null;
    },
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createAssignEmailCycleStore = (): Instance<
  typeof AssignEmailCycleStore
> => AssignEmailCycleStore.create(getInitialState());
